import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { CircularProgress, Box, Button, Typography, Stack } from '@mui/material'
import { AttachMoney, CheckCircleRounded, DateRange, Error, TaskAlt, Notes, PanoramaFishEye } from '@mui/icons-material'
import { FormatCurrency, FormatDateValue, SpaceBeforeCapitals } from 'core/Utilities'
import { LabelWithToolTip } from 'Components/Label/Index'
import 'core/styles.css'
import { ProgramVerificationStatus, type ProgramVerificationDetails } from 'core/types'
import { generatePath, useNavigate } from 'react-router'
import { PROGRAM_VERIFICATION } from 'Pages/Routes'
import { useRefresh } from 'ContextProviders/RefreshProvider'
import { IncomeApprovalCard } from 'Components/Program/IncomeApprovalCard'
import { ReportedIncome } from 'Components/ChildDetails/ReportedIncome'

export interface ProgramVerificationProps {
  verificationId: string
  doNotDisplayAsCard?: boolean
  hideViewButton?: boolean
  readOnly?: boolean
  showHeader?: boolean
  showIncome?: boolean
  redirectText?: string
}

export function ProgramVerification (props: ProgramVerificationProps): JSX.Element {
  const [details, setDetails] = useState<ProgramVerificationDetails | null>(null)
  const refreshTime = useRefresh()
  const nav = useNavigate()
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/ProgramVerification/Find?id=${props.verificationId}`, {})
      if (success) {
        setDetails(response)
      }
    }
    if (props.verificationId != null && props.verificationId !== '') { void loadData() } else {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setDetails({ status: 'NotVerified' } as ProgramVerificationDetails)
    }
  }, [props.verificationId, refreshTime])

  const displayIcon = (status: ProgramVerificationStatus): JSX.Element => {
    if (status === ProgramVerificationStatus.Verified) {
      return <CheckCircleRounded htmlColor='#7136BC' fontSize='large' width='15%' />
    } else if (status === ProgramVerificationStatus.NotVerified) {
      return <Error htmlColor='#BC3656' fontSize='large' width='15%' />
    } else {
      return <PanoramaFishEye htmlColor='#7136BC' fontSize='large' width='15%' /> // PanoramaFishEye is just a circle
    }
  }

  const viewVerification = (): void => {
    const route = generatePath(PROGRAM_VERIFICATION, { id: props.verificationId })
    nav(route, { state: { readOnly: true, redirectText: props.redirectText } })
  }

  const modifyVerification = (): void => {
    const route = generatePath(PROGRAM_VERIFICATION, { id: props.verificationId })
    nav(route, { state: { redirectText: props.redirectText } })
  }

  return (details === null
    ? <CircularProgress />
    : <Box className={props.doNotDisplayAsCard == null || !props.doNotDisplayAsCard ? 'CardDisplay' : 'ComponentSubCard'} >
      {props.showHeader === true && <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>Program Details</Typography>}
      <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='start' marginBottom={2}>
        {displayIcon(details.status)}
        <Stack direction='column' width='85%' m='0px !important'>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{details.programName} - {SpaceBeforeCapitals(details.status)}</Typography>
          <Typography variant='body1'>{details.timeSpanName}</Typography>
        </Stack>
      </Stack>
      {(props.showIncome ?? false) && (

        <Stack>
          <ReportedIncome id={details.reportedIncomeID} childID={details.childID} canEdit={!(props.readOnly ?? false) && props.verificationId !== ''} />
        </Stack>
      )}
      {details.status !== ProgramVerificationStatus.NotVerified && <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
        <Stack direction='column' spacing={1}>
          {details.verifiedAnnualIncome != null && details.verifiedAnnualIncome !== 0 && <Stack direction='row' spacing={1}>
            <AttachMoney />
            <Typography>{FormatCurrency(details.verifiedAnnualIncome)}</Typography>
          </Stack>
          }
          <Stack direction='row' spacing={1}>
            <DateRange />
            <Typography>{details.timeSpanName}</Typography>
          </Stack>
          <Stack direction='row' spacing={1}>
            <TaskAlt />
            <Typography>{details.isValid ? 'Valid' : 'Not Valid'}</Typography>
          </Stack>
        </Stack>
        <Stack direction='column' spacing={1}>
          {details.verifiedFederalPovertyLevel != null && details.verifiedFederalPovertyLevel !== '0%' && <Stack direction='row' spacing={1}>
            <Typography sx={{ fontWeight: 'bold' }}>FPL %</Typography>
            <Typography>{details.verifiedFederalPovertyLevel}</Typography>
          </Stack>
          }
          {details.memberCount != null && details.memberCount !== 0 && <Stack direction='row' spacing={1}>
            <Typography sx={{ fontWeight: 'bold' }}>Members</Typography>
            <Typography>{details.memberCount}</Typography>
          </Stack>
          }
          <Stack direction='row' spacing={1}>
            <Typography sx={{ fontWeight: 'bold' }}>Expires</Typography>
            <Typography>{details.expireDate !== null ? FormatDateValue(details.expireDate) : 'Never'}</Typography>
          </Stack>
        </Stack>
      </Stack>}
      {details.notes != null && details.notes !== '' && <Stack direction='row' spacing={1} marginTop={1}>
        <Notes />
        <Typography>{details.notes}</Typography>
      </Stack>}
      {details.status !== ProgramVerificationStatus.NotVerified && details.incomeApprovals.length !== 0 &&
        <div style={{ marginTop: 10 }}>
          <LabelWithToolTip labelText='Income Approvals' toolTipText='This verification requires an additional approval.  You can review the status of that approval here.' />
          {details.incomeApprovals.map((approval) => {
            return <IncomeApprovalCard approval={approval} canEdit={approval.approved === false && approval.reasonID == null } />
          })
          }
        </div>
      }
      {props.hideViewButton !== true && props.verificationId !== '' && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          {details.status === ProgramVerificationStatus.Verified || props.readOnly === true
            ? <Button variant='contained' color='primary' sx={{ px: 4, textTransform: 'none' }} data-testid='viewVerificationButton' onClick={viewVerification}> View Verification</Button>
            : <Button variant='contained' color='primary' sx={{ px: 4, textTransform: 'none' }} data-testid='modifyVerificationButton' onClick={modifyVerification}> Modify Verification</Button>
          }
        </div>
      )}
    </Box >
  )
}
